import { useCallback, useState } from 'react';
import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import useQueryParameters from 'hooks/useQueryParameters';

import { transformDob } from 'utils/dateUtils';
import { useSelector } from 'react-redux';
import { selectTenant, isOfferConfigured } from 'state/tenant';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';

export interface UnifiedBuyerParams {
  mobilePhone: string;
  emailAddress: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  firstName: string;
  lastName: string;
  middleInitial?: string;
  ssn: string | null;
  dateOfBirth: string | null;
}

export class UnifiedBuyerError extends Error {}

type UnifiedBuyerHook = () => [
  (params: UnifiedBuyerParams) => Promise<void>,
  Status,
  boolean,
];
const PATH = '/api/buyer';

const useUnifiedBuyer: UnifiedBuyerHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { mockBuyer } = useQueryParameters();
  const { bread } = useSelector(selectTenant);
  const overrideAttributes = useApplicationBasedOverrides();

  const unifiedBuyer = useCallback(
    async (params: UnifiedBuyerParams) => {
      setError(false);

      if (
        !isOfferConfigured('installments', bread) &&
        !isOfferConfigured('splitpay', bread)
      ) {
        return Promise.resolve();
      }
      params = transformDob(['dateOfBirth'], params);

      const payload = {
        ...params,
        ...overrideAttributes,
        mockResponse: mockBuyer,
      };

      const response = await fetch(PATH, {
        method: 'POST',
        body: payload,
      });

      if (!response.ok) {
        setError(true);
        return Promise.reject(new UnifiedBuyerError());
      }
    },
    [bread, fetch, mockBuyer, overrideAttributes],
  );
  return [unifiedBuyer, status, error];
};

export default useUnifiedBuyer;
